import { useContext, useState, useEffect } from "react";
import { ImagePicker } from "../../../../components/common";
import Avatar from "../../../../components/avatar";
import useRequest from "../../../../api/useRequest";
import { BrandKitRequestFactory, CustomFontRequestFactory} from "../../../../api/requestFactory";
import { NotificationContext } from "../../../../context/notification-context";
import FontPicker from "../../../../components/common/font-picker";

const colorOptions = [
  "#2cd4bf",
  "#38bcf7",
  "#feb022",
  "#f87066",
  "#33d583",
  "#818cf8",
  "#f471b6",
  "#bf84fc",
];

export default function NewBrandKitCreationModal({ onClose, onSave, oldBrandKit, saveBrandKitOverride, addCustomFontOverride, getCustomFontsOverride }) {
  const [brandKit, setBrandKit] = useState(
    oldBrandKit?.id
      ? {
          name: oldBrandKit.name,
          handle: oldBrandKit.handle,
          logo: oldBrandKit.logo,
          font: oldBrandKit.font,
          customFont: oldBrandKit.customFont || null,
          primary_color: oldBrandKit.primary_color,
          secondary_color: oldBrandKit.secondary_color,
          tertiary_color: oldBrandKit.tertiary_color,
          secondary_font: oldBrandKit.secondary_font,
        }
      : {
          name: "",
          handle: "",
          logo: "",
          font: "",
          secondary_font: "",
          primary_color: "#000000",
          secondary_color: "#000000",
          tertiary_color: "#000000",
          customFont: null,
        }
  );
  // Track the logo's MediaItem id separately from the display URL so the
  // save can pass `media_ids` and the BE can both derive the durable
  // object_key for `logo` AND create the media_attachments join row. The
  // signed URL alone would survive via StorageResolvable's URL→key
  // recovery, but the attachment row would never get created — leaving
  // an orphan MediaItem.
  const [logoMediaId, setLogoMediaId] = useState(
    oldBrandKit?.media_attachments?.[0]?.id || null
  );

  const { requestMaker } = useRequest();
  const { showNotification } = useContext(NotificationContext);
  const [customFonts, setCustomFonts] = useState([]);


  useEffect(() => {
    const customFontsRequest = getCustomFontsOverride
      ? getCustomFontsOverride()
      : CustomFontRequestFactory.createGetUserCustomFontsRequest();
    requestMaker(customFontsRequest)
      .then((data) => {
        const userCustomFonts = data.custom_fonts.map((font) => ({
          fontFamily: font.name,
          fontUrl: font.font_url,
          created_at: font.created_at,
        }));
        setCustomFonts(userCustomFonts);
      })
      .catch((err) => {
        showNotification(
          {
            title: "Something went wrong",
            content: "Cannot fetch user details, please try again.",
          },
          "error"
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestMaker]);


  function saveBrandKit() {
    const payload = {
      ...brandKit,
      media_ids: logoMediaId ? [logoMediaId] : [],
    };
    let request;
    if (oldBrandKit?.id) {
      request = BrandKitRequestFactory.createUpdateBrandKitRequest(oldBrandKit.id, payload);
    } else if (saveBrandKitOverride) {
      request = saveBrandKitOverride(payload);
    } else {
      request = BrandKitRequestFactory.createSaveBrandKitRequest(payload);
    }

    requestMaker(request)
      .then((res) => {
        showNotification(
          {
            title: oldBrandKit?.id ? "Brand Kit Updated" : "Brand Kit Created",
            content: "Your brand kit has been saved successfully",
          },
          "success"
        );
        if (onSave) {
          onSave(res.brand_kit || { ...brandKit, id: res.id });
        } else {
          window.location.reload();
        }
      })
      .catch((err) => {
        showNotification(
          {
            title: "Something went wrong",
            content:
              err?.response?.data?.errors?.[0] || "Please try again later",
          },
          "error"
        );
      });
  }

  return (
    <div className="fixed inset-0 z-40 grid w-screen h-screen p-4 place-items-center bg-gray-700/75 sm:p-6 lg:p-8">
      <div className="relative w-full max-h-[90vh] overflow-y-scroll max-w-xl p-6 space-y-6 mx-auto overflow-hidden bg-white shadow-xl rounded-xl">
        <div className="flex justify-between items-center">
          <p className="text-xl font-semibold text-gray-900">
            Create Brand Kit
          </p>

          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 bg-white rounded-lg hover:text-gray-500 focus:outline-none"
          >
            <span className="sr-only">Close Modal</span>
            <svg
              aria-hidden="true"
              className="w-6 h-6"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="flex flex-col gap-5 ">
          <div className="flex flex-col gap-1.5 ">
            <p className="text-sm font-medium text-gray-900">Profile Name</p>
            <input
              placeholder="Enter your profile name"
              className="block w-full px-3 py-2 text-gray-900 border-0 shadow-sm rounded-xl ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6 caret-blue-500 focus:outline-none"
              value={brandKit.name}
              onChange={(e) =>
                setBrandKit({ ...brandKit, name: e.target.value })
              }
            />
          </div>

          <div className="flex flex-col gap-1.5 ">
            <p className="text-sm font-medium text-gray-900">Profile Handle</p>
            <input
              placeholder="@profilehandle"
              className="block w-full px-3 py-2 text-gray-900 border-0 shadow-sm rounded-xl ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6 caret-blue-500 focus:outline-none"
              value={brandKit.handle}
              onChange={(e) =>
                setBrandKit({ ...brandKit, handle: e.target.value })
              }
            />
          </div>

          <div className="flex flex-col gap-1.5 ">
            <p className="text-sm font-medium text-gray-900">
              Profile Image
              <span className="font-normal text-gray-500">
                {" "}
                (JPG or PNG 256x256px)
              </span>
            </p>
            <div>
              <div className="mt-1.5 flex items-center gap-4">
                <Avatar
                  imageStyle="w-12 h-12 bg-gray-100 rounded-lg border border-gray-200"
                  url={brandKit.logo}
                />
                {brandKit.logo === "" && (
                  <>
                    <ImagePicker
                      onSelect={(ev) => {
                        const displayUrl = ev.preview_url || ev.url;
                        setBrandKit({ ...brandKit, logo: displayUrl });
                        setLogoMediaId(ev.media_item?.id || null);
                      }}
                      onError={(error) => {
                        showNotification(
                          {
                            title: "Something went wrong",
                            content: error.message,
                          },
                          "error"
                        );
                      }}
                      className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-gray-600 transition-all duration-150 bg-white rounded-full shadow-xs hover:text-blue-900 hover:bg-gray-50 ring-1 ring-inset ring-gray-300 px-4 py-1.5"
                    >
                      Select Image
                    </ImagePicker>
                  </>
                )}
                {brandKit.logo !== "" && (
                  <button
                    onClick={() => {
                      setBrandKit({ ...brandKit, logo: "" });
                      setLogoMediaId(null);
                    }}
                    type="button"
                    className="inline-flex items-center justify-center w-8 h-8 transition-all duration-150 rounded-full text-error-500 hover:bg-error-50 hover:text-error-600"
                  >
                    <span className="sr-only">Delete Image</span>
                    <svg
                      aria-hidden="true"
                      className="w-5 h-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-1.5 ">
            <p className="text-sm font-medium text-gray-900">
              Primary Font Family
            </p>
            <div className="flex items-center gap-4">
              <FontPicker
                onSelect={(val) => {
                  setBrandKit({ ...brandKit, font: val, customFont: null });
                }}
                defaultValue={brandKit.font}
                customFonts={customFonts}
                setCustomFonts={setCustomFonts}
                addCustomFontOverride={addCustomFontOverride}
              />
            </div>{" "}
          </div>

          <div className="flex flex-col gap-1.5 ">
            <p className="text-sm font-medium text-gray-900">
              Secondary Font Family
            </p>
            <FontPicker
              onSelect={(val) => {
                setBrandKit({ ...brandKit, secondary_font: val });
              }}
              defaultValue={brandKit.secondary_font}
              customFonts={customFonts}
              addCustomFontOverride={addCustomFontOverride}
            />
          </div>
          <div className="flex flex-col gap-1.5 ">
            <p className="text-sm font-medium text-gray-900">
              Background Color
            </p>
            <div className="grid grid-cols-8 gap-2">
              {colorOptions.map((color, color_index) => {
                return (
                  <button
                    key={color_index}
                    type="button"
                    className="w-full rounded h-7"
                    onClick={() => {
                      setBrandKit({
                        ...brandKit,
                        primary_color: color,
                      });
                    }}
                    style={{ backgroundColor: color }}
                  ></button>
                );
              })}
            </div>
            <div className="relative">
              <input
                type="text"
                name=""
                id=""
                placeholder=""
                value={brandKit.primary_color}
                onChange={(e) => {
                  setBrandKit({
                    ...brandKit,
                    primary_color: e.target.value,
                  });
                }}
                className="block w-full py-2 pl-3 pr-10 text-gray-900 border-0 shadow-sm rounded-xl ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6 caret-blue-500"
              />

              <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                <div
                  className="w-8 h-6 rounded"
                  style={{ backgroundColor: brandKit.primary_color }}
                ></div>
                <input
                  type="color"
                  name=""
                  id=""
                  placeholder=""
                  value={brandKit.primary_color}
                  onChange={(e) => {
                    setBrandKit({
                      ...brandKit,
                      primary_color: e.target.value,
                    });
                  }}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
              </div>
            </div>
          </div>

          <div className="flex gap-6 items-center justify-between">
            <div className="flex flex-col gap-1.5 ">
              <p className="text-sm font-medium text-gray-900">Primary Color</p>

              <div className="relative">
                <input
                  type="text"
                  name=""
                  id=""
                  placeholder=""
                  value={brandKit.secondary_color}
                  onChange={(e) => {
                    setBrandKit({
                      ...brandKit,
                      secondary_color: e.target.value,
                    });
                  }}
                  className="block w-full py-2 pl-3 pr-10 text-gray-900 border-0 shadow-sm rounded-xl ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6 caret-blue-500"
                />

                <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <div
                    className="w-8 h-6 rounded"
                    style={{ backgroundColor: brandKit.secondary_color }}
                  ></div>
                  <input
                    type="color"
                    name=""
                    id=""
                    placeholder=""
                    value={brandKit.primary_color}
                    onChange={(e) => {
                      setBrandKit({
                        ...brandKit,
                        secondary_color: e.target.value,
                      });
                    }}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-1.5 ">
              <p className="text-sm font-medium text-gray-900">
                Secondary Color
              </p>
              <div className="relative">
                <input
                  type="text"
                  name=""
                  id=""
                  placeholder=""
                  value={brandKit.tertiary_color}
                  onChange={(e) => {
                    setBrandKit({
                      ...brandKit,
                      tertiary_color: e.target.value,
                    });
                  }}
                  className="block w-full py-2 pl-3 pr-10 text-gray-900 border-0 shadow-sm rounded-xl ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6 caret-blue-500"
                />

                <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <div
                    className="w-8 h-6 rounded"
                    style={{ backgroundColor: brandKit.tertiary_color }}
                  ></div>
                  <input
                    type="color"
                    name=""
                    id=""
                    placeholder=""
                    value={brandKit.tertiary_color}
                    onChange={(e) => {
                      setBrandKit({
                        ...brandKit,
                        tertiary_color: e.target.value,
                      });
                    }}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end items-center gap-3">
          <button
            onClick={onClose}
            className="h-fit py-2.5 px-4 text-sm font-medium transition-all duration-150 rounded-full bg-white disabled:text-gray-400 text-gray-700 shadow-xs ring-1 hover:text-gray-900 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            Discard
          </button>
          <button
            onClick={() => saveBrandKit()}
            className="inline-flex items-center justify-center gap-2 transition-all duration-150 rounded-full px-3 py-2 sm:px-4 sm:py-2.5 text-sm font-semibold hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 bg-blue-500 text-white"
          >
            Save Brand Kit
          </button>
        </div>
      </div>
    </div>
  );
}
